// apps/admin/src/pages/reports/AuditLogsPage.tsx
//
// Phase 8.3 Wave 5 Task 39 — Audit Logs page (Recent + Archive Search).
//
// Route: /reports/audit-logs
//
// vg-binding: CONTEXT:decisions=P8.D-113,P8.D-138,P8.D-191,P8.D-222,P8.D-258,P8.D-480
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Scope (per PLAN/task-39.md):
//   - Two tabs:
//       "Recent (≤180 ngày)" → AuditLogTable (warm tier API)
//       "Archive Search (>180 ngày, cold S3)" → ArchiveSearchTab
//   - Filters (24-entity dropdown, actor, action, date
//     range, severity, q).
//   - Immutable banner top — read-only; no Edit/Delete actions.
//   - Row click → side drawer with full audit-entry detail + DiffViewer
//     (flat key-path, max depth 3, "Xem JSON đầy đủ").
//   - Export button → routes to /reports/export-center (admin Audit Export
//     requires dual approval flow). Wired here as
//     a navigation hint — the actual export-center page lands in task-40.
//   - URL state sync — `?tab=recent|archive` controls which tab is active.
//     Warm filters use the existing query params (actor_user_id, action,
//     target_type, date_from, date_to, severity, q). Restoring from URL =
//     deeplink contract for shareable filter views.

import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { usePermission, PageHeader } from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ensureP83Loaded } from '../../i18n/index.js';
import {
  AuditLogTable,
  type AuditWarmFilterValue,
} from '../../features/audit/AuditLogTable.js';
import { ArchiveSearchTab } from '../../features/audit/ArchiveSearchTab.js';
import { DiffViewer } from '../../features/audit/DiffViewer.js';
import { actionLabel } from '../../features/audit/action-label.js';
import type {
  AuditV2EntityType,
  AuditV2Row,
  AuditV2Severity,
  AuditV2ActorRole,
} from '../../api/audit-log-v2.api.js';

// ---------------------------------------------------------------------------
// URL state helpers — Recent tab filters sync 1:1 with query params.
// ---------------------------------------------------------------------------

type TabId = 'recent' | 'archive';

function readTabFromUrl(sp: URLSearchParams): TabId {
  const raw = sp.get('tab');
  return raw === 'archive' ? 'archive' : 'recent';
}

function readCsvList(raw: string | null): string[] | undefined {
  if (!raw) return undefined;
  const items = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return items.length > 0 ? items : undefined;
}

function readWarmFilterFromUrl(sp: URLSearchParams): AuditWarmFilterValue {
  const out: AuditWarmFilterValue = {};
  const actorUid = sp.get('actor_user_id');
  if (actorUid) out.actor_user_id = actorUid;
  const actorRole = sp.get('actor_role');
  if (actorRole) out.actor_role = actorRole as AuditV2ActorRole;
  // Round-13 (2026-05-23): `target_type` + `action` are CSV multi-values.
  const targetTypeList = readCsvList(sp.get('target_type'));
  if (targetTypeList) out.target_type = targetTypeList as AuditV2EntityType[];
  const actionList = readCsvList(sp.get('action'));
  if (actionList) out.action = actionList;
  const dateFrom = sp.get('date_from');
  if (dateFrom) out.date_from = dateFrom;
  const dateTo = sp.get('date_to');
  if (dateTo) out.date_to = dateTo;
  const sev = sp.get('severity');
  if (sev) out.severity = sev as AuditV2Severity;
  const q = sp.get('q');
  if (q) out.q = q;
  return out;
}

function writeUrl(
  base: URLSearchParams,
  tab: TabId,
  warm: AuditWarmFilterValue,
): URLSearchParams {
  const sp = new URLSearchParams(base);
  // Wipe filter keys, then re-write only the active ones — keeps the URL
  // short and avoids stale params lingering across tab switches.
  for (const k of [
    'actor_user_id',
    'actor_role',
    'target_type',
    'action',
    'date_from',
    'date_to',
    'severity',
    'q',
  ]) {
    sp.delete(k);
  }
  sp.set('tab', tab);
  for (const [k, v] of Object.entries(warm)) {
    if (typeof v === 'string' && v.length > 0) sp.set(k, v);
    else if (Array.isArray(v) && v.length > 0) sp.set(k, v.join(','));
  }
  return sp;
}

// ---------------------------------------------------------------------------
// Detail drawer
// ---------------------------------------------------------------------------

interface DrawerProps {
  row: AuditV2Row | null;
  onClose: () => void;
}

function DetailDrawer({ row, onClose }: DrawerProps): JSX.Element | null {
  const { t } = useTranslation('p83');
  const { t: tOps } = useTranslation('ops');
  if (!row) return null;
  // Reconstruct raw before/after objects from the flat diff array for the
  // "Xem JSON đầy đủ" affordance — the BE doesn't ship the raw payload so
  // we materialize it from the diff entries (shallow approximation).
  const rawBefore: Record<string, unknown> = {};
  const rawAfter: Record<string, unknown> = {};
  for (const d of row.diff ?? []) {
    rawBefore[d.path] = d.before;
    rawAfter[d.path] = d.after;
  }
  return (
    <div
      className="fixed inset-0 z-40 flex"
      role="dialog"
      aria-modal="true"
      aria-label={t('reports.auditLogs.drawer.aria')}
      data-testid="audit-detail-drawer"
    >
      <button
        type="button"
        onClick={onClose}
        className="flex-1 bg-black/40" /* dark-ok: modal backdrop scrim, theme-agnostic (P999.11.D-04) */
        aria-label={t('reports.auditLogs.drawer.closeAria')}
        data-testid="audit-detail-backdrop"
      />
      <aside className="flex h-full w-full max-w-3xl flex-col gap-3 overflow-y-auto bg-pw-bg p-5 shadow-2xl">
        <header className="flex items-start justify-between gap-2 border-b border-pw-border pb-2">
          <div>
            <h2 className="text-lg font-semibold text-pw-text">
              {t('reports.auditLogs.drawer.heading', { action: actionLabel(tOps, row.action) })}
            </h2>
            <p className="text-xs text-pw-text-muted">
              {tOps(`audit.entityTypeLabels.${row.target_type}`, { defaultValue: row.target_type })} • {row.target_id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-pw-border bg-pw-bg px-2.5 py-1 text-sm font-medium text-pw-text hover:bg-pw-surface"
            data-testid="audit-detail-close"
          >
            {t('reports.auditLogs.drawer.close')}
          </button>
        </header>
        <dl className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <Field label={t('reports.auditLogs.drawer.fields.eventId')} value={row.event_id} mono />
          <Field
            label={t('reports.auditLogs.drawer.fields.occurredAt')}
            value={new Date(row.occurred_at).toLocaleString()}
          />
          <Field label={t('reports.auditLogs.drawer.fields.actorUser')} value={row.actor_user_id ?? '—'} mono />
          <Field label={t('reports.auditLogs.drawer.fields.actorRole')} value={row.actor_role} />
          <Field label={t('reports.auditLogs.drawer.fields.actorOrg')} value={row.actor_org_id ?? '—'} mono />
          <Field label={t('reports.auditLogs.drawer.fields.actorOrgType')} value={row.actor_org_type ?? '—'} />
          <Field label={t('reports.auditLogs.drawer.fields.ip')} value={row.ip ?? '—'} mono />
          <Field label={t('reports.auditLogs.drawer.fields.userAgent')} value={row.user_agent ?? '—'} mono small />
          <Field label={t('reports.auditLogs.drawer.fields.severity')} value={row.severity} />
          <Field
            label={t('reports.auditLogs.drawer.fields.target')}
            value={`${row.target_type} • ${row.target_id}`}
            mono
          />
        </dl>
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-pw-text">
            {t('reports.auditLogs.drawer.diffHeading')}
          </h3>
          <DiffViewer
            diff={row.diff ?? []}
            truncated={row.diff_truncated}
            rawBefore={rawBefore}
            rawAfter={rawAfter}
          />
        </section>
      </aside>
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
  small = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  small?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] uppercase tracking-wide text-pw-text-muted">
        {label}
      </dt>
      <dd
        className={[
          mono ? 'font-mono' : '',
          small ? 'text-xs' : 'text-sm',
          'break-words text-pw-text',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AuditLogsPage(): JSX.Element {
  const { t, locale } = useTranslation('p83');
  useEffect(() => {
    void ensureP83Loaded(locale);
  }, [locale]);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = readTabFromUrl(searchParams);
  const warmFilter = useMemo(
    () => readWarmFilterFromUrl(searchParams),
    [searchParams],
  );
  const [active, setActive] = useState<AuditV2Row | null>(null);

  // Gate Archive Search tab on FE perm — BE re-checks server-side, so this
  // is a UX affordance only (cold-tier permission).
  const canArchiveSearch = usePermission('audit:archive_search');

  const setTab = useCallback(
    (next: TabId): void => {
      const sp = writeUrl(searchParams, next, warmFilter);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams, warmFilter],
  );

  const setWarmFilter = useCallback(
    (next: AuditWarmFilterValue): void => {
      const sp = writeUrl(searchParams, tab, next);
      // Filter change resets to page 1 — keeps users off empty deep pages.
      sp.delete('page');
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams, tab],
  );

  // Round-14: `?page=` owned by URL so deeplinks survive refresh.
  const rawPage = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? rawPage : 1;
  const setPage = useCallback(
    (next: number): void => {
      const sp = new URLSearchParams(searchParams);
      if (next <= 1) sp.delete('page');
      else sp.set('page', String(next));
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Defensive — if URL says `tab=archive` but the user lost the permission
  // mid-session (rare), bounce back to recent. Server still 403s.
  useEffect(() => {
    if (tab === 'archive' && !canArchiveSearch) {
      setTab('recent');
    }
  }, [tab, canArchiveSearch, setTab]);

  const tabs: TabId[] = canArchiveSearch ? ['recent', 'archive'] : ['recent'];

  return (
    <main className="flex flex-col gap-4 p-6" data-testid="audit-logs-page">
      <PageHeader
        title={t('reports.auditLogs.title')}
        subtitle={t('reports.auditLogs.pageSubtitle')}
      />

      {/* Immutable banner */}
      <div
        className="rounded-md border border-pw-border bg-pw-surface px-3 py-2 text-xs text-pw-text"
        role="status"
        data-testid="audit-immutable-banner"
      >
        <strong className="font-semibold">
          {t('reports.auditLogs.banner.label')}
        </strong>{' '}
        {t('reports.auditLogs.banner.body')}
      </div>

      {/* Tabs */}
      <div
        role="tablist"
        aria-label={t('reports.auditLogs.tabsAria')}
        className="flex flex-wrap items-center gap-1 border-b border-pw-border"
        data-testid="audit-tabs"
      >
        {tabs.map((id) => {
          const isActive = id === tab;
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-controls={`audit-tabpanel-${id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setTab(id)}
              className={[
                'inline-flex items-center whitespace-nowrap px-3 py-2',
                'text-sm font-medium transition-colors',
                'border-b-2 -mb-px',
                'focus:outline-none focus:ring-2 focus:ring-pw-primary/40 focus:ring-offset-1',
                isActive
                  ? 'border-pw-primary text-pw-primary-strong'
                  : 'border-transparent text-pw-text-muted hover:text-pw-text hover:border-pw-border',
              ].join(' ')}
              data-testid={`audit-tab-${id}`}
              data-active={isActive ? 'true' : 'false'}
            >
              {t(`reports.auditLogs.tabLabels.${id}`)}
            </button>
          );
        })}

        <div className="ml-auto flex items-center gap-2 pb-2">
          <Link
            to="/reports/export-center?source=audit"
            className="inline-flex h-9 items-center justify-center rounded-md border border-pw-border bg-pw-bg px-3 text-sm font-medium text-pw-text transition-colors hover:bg-pw-surface"
            data-testid="audit-export-link"
            title={t('reports.auditLogs.exportTitle')}
          >
            {t('reports.auditLogs.exportCta')}
          </Link>
        </div>
      </div>

      {/* Tab panels */}
      {tab === 'recent' ? (
        <div
          role="tabpanel"
          id="audit-tabpanel-recent"
          aria-labelledby="audit-tab-recent"
        >
          <AuditLogTable
            filter={warmFilter}
            onFilterChange={setWarmFilter}
            onRowClick={setActive}
            page={page}
            onPageChange={setPage}
          />
        </div>
      ) : null}
      {tab === 'archive' && canArchiveSearch ? (
        <div
          role="tabpanel"
          id="audit-tabpanel-archive"
          aria-labelledby="audit-tab-archive"
        >
          <ArchiveSearchTab onRowClick={setActive} />
        </div>
      ) : null}

      <DetailDrawer row={active} onClose={() => setActive(null)} />
    </main>
  );
}

export default AuditLogsPage;
